import { useState, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CONSENT_VERSION } from "./utils/consent";

// PERF FIX (2026-08-13): these used to be regular top-of-file imports,
// which meant the browser had to download the code for EVERY screen
// (Dashboard, TenantScreen, Calendar, etc.) before it could show even
// the login page — one giant bundle, ~2.3MB, took ~14s on its own.
// lazy() instead makes each screen its own small file that's only
// downloaded the moment it's actually about to be shown (e.g. Dashboard's
// code isn't fetched until someone has actually logged in). Login itself
// gets to the screen much faster, since it no longer waits on code for
// screens the person isn't even looking at yet.
const LoginScreen          = lazy(() => import("./screens/LoginScreen"));
const Dashboard            = lazy(() => import("./screens/Dashboard"));
const ResetPasswordScreen  = lazy(() => import("./screens/ResetPasswordScreen"));
const EmailChangeScreen    = lazy(() => import("./screens/EmailChangeScreen"));
const TenantScreen         = lazy(() => import("./screens/TenantScreen"));
const Calendar              = lazy(() => import("./screens/calendar"));
const ForcedPasswordChange  = lazy(() => import("./screens/ForcedPasswordChange"));
const ConsentScreen         = lazy(() => import("./screens/ConsentScreen"));

// ── Trial Warning Popup ────────────────────────────────────────
function TrialWarningPopup({ daysLeft, onConfirm }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 10000,
    }}>
      <div style={{
        background: "#1a1a2e", border: "1px solid #2e2e4e", borderRadius: "10px",
        padding: "24px", maxWidth: "360px", textAlign: "center",
        fontFamily: "'Segoe UI', Arial, sans-serif",
      }}>
        <div style={{ fontSize: "28px", marginBottom: "8px" }}>⏳</div>
        <div style={{ color: "#f0c040", fontSize: "16px", fontWeight: "700", marginBottom: "8px" }}>
          Trial ending soon
        </div>
        <div style={{ color: "#c8c8d8", fontSize: "13px", marginBottom: "20px" }}>
          Your trial ends in {daysLeft} day{daysLeft === 1 ? "" : "s"}. Contact support@adyantam.in to continue after that.
        </div>
        <button
          onClick={onConfirm}
          style={{
            background: "#6c63ff", color: "#fff", border: "none",
            borderRadius: "6px", padding: "10px 24px", fontSize: "14px",
            fontWeight: "600", cursor: "pointer",
          }}
        >
          OK, got it
        </button>
      </div>
    </div>
  );
}

// ── Splash / loading screen — shown both while auth state is being
// ── restored AND (via Suspense) while a lazy-loaded screen's code is
// ── still downloading, so switching screens never shows a blank page.
function LoadingSplash() {
  return (
    <div style={{
      background: "#0f0f13", minHeight: "100vh",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: "12px",
      fontFamily: "'Segoe UI', Arial, sans-serif",
    }}>
      <div style={{ fontSize: "32px" }}>🏢</div>
      <div style={{ color: "#6c63ff", fontSize: "14px", fontWeight: "600" }}>Adyantam</div>
      <div style={{ color: "#555570", fontSize: "12px" }}>Loading…</div>
    </div>
  );
}

// ── Route guard ────────────────────────────────────────────────
function AppRoutes() {
  const { user, loading, dpdpEnabled } = useAuth();
  const [trialWarningDismissed, setTrialWarningDismissed] = useState(false);

  // Public route — Reset Password page, accessible without login
  // Link type: Firebase puts it in the address as ?mode=... (works on any address)
  const linkMode = new URLSearchParams(window.location.search).get("mode");
  const isEmailChangeRoute = linkMode === "verifyAndChangeEmail";
  // Reset screen: new-style link (mode=resetPassword) or old /reset-password address
  const isResetPasswordRoute =
    !isEmailChangeRoute &&
    (linkMode === "resetPassword" || window.location.pathname === "/reset-password");

  // Public route — Reset Password page works without login checks
  // Public route: email-change confirmation link, works without login
  if (isEmailChangeRoute) {
    return <EmailChangeScreen />;
  }

  if (isResetPasswordRoute) {
    return <ResetPasswordScreen />;
  }

  // Splash / loading screen
  if (loading) {
    return <LoadingSplash />;
  }

  // Forced password change — must clear before any app content
  if (user?.mustChangePassword) {
    return <ForcedPasswordChange />;
  }

  // DPDP 2: privacy notice + consent — must be agreed (for the current
  // notice version) before any app content, for every role including tenants.
  // The DPDP switch (set from the CRM) can turn this off; while it is still
  // being checked (null) we wait, so the app never flashes before consent.
  if (user && dpdpEnabled === null) {
    return <LoadingSplash />;
  }
  if (user && dpdpEnabled && user.consentVersion !== CONSENT_VERSION) {
    return <ConsentScreen />;
  }

  // Tenant member — separate dashboard
  if (user?.memberType === "tenant") {
    return (
      <>
        <Calendar />
        <TenantScreen />
      </>
    );
  }

  // Trial warning — show once after login, before normal app flow
  if (user && user.trialWarningDaysLeft != null && !trialWarningDismissed) {
    return (
      <TrialWarningPopup
        daysLeft={user.trialWarningDaysLeft}
        onConfirm={() => setTrialWarningDismissed(true)}
      />
    );
  }

  // Normal app flow
  return user ? (
    <>
      <Calendar />
      <Dashboard />
    </>
  ) : <LoginScreen />;
}

// ── Scrolling Banner ───────────────────────────────────────────
const bannerStyles = `
  @keyframes marquee {
    0%   { transform: translateX(100vw); }
    100% { transform: translateX(-100%); }
  }
  .abc-marquee {
    display: inline-block;
    white-space: nowrap;
    animation: marquee 35s linear infinite;
  }
`;

function ScrollingBanner() {
  return (
    <>
      <style>{bannerStyles}</style>
      <div style={{
        background: "#1a1a2e",
        borderBottom: "1px solid #2e2e4e",
        overflow: "hidden",
        height: "28px",
        lineHeight: "28px",
        padding: 0,
        position: "sticky",
        top: 0,
        zIndex: 9999,
        width: "100%",
      }}>
        <span className="abc-marquee" style={{
          color: "#f0c040",
          fontSize: "12px",
          fontWeight: "600",
          fontFamily: "'Segoe UI', Arial, sans-serif",
          letterSpacing: "0.4px",
        }}>
          🏛️ &nbsp; Honesty, Transparency and Discipline are the foundation for this application &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </span>
      </div>
    </>
  );
}

// ── Root ───────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <ScrollingBanner />
      {/* Suspense catches the brief gap while a lazy-loaded screen's own
          code is still downloading (e.g. Dashboard.jsx right after login).
          Without this, React throws instead of rendering anything for that
          gap — this shows the same splash screen used during auth-restore. */}
      <Suspense fallback={<LoadingSplash />}>
        <AppRoutes />
      </Suspense>
    </AuthProvider>
  );
}
